import PropTypes from "prop-types";
import { useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import Iconify from "../../../../../reusable-components/iconify";
import { fDateTimeWithTimezone } from "../../../../../utils/formatTime";
import { parseLogItems, listItemStyles, getItemDescription } from "./orderLogHelper";
import OrderLogItemSubstitution from "./OrderLogItemSubstitution";

InvoiceItemUpdate.propTypes = {
  update: PropTypes.object,
  setHighlightedRow: PropTypes.func,
  items: PropTypes.array,
};
function InvoiceItemUpdate({ update, setHighlightedRow, items }) {
  const formatColumnName = (columnName) => {
    if(!columnName) return "";
    const spaced = columnName
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2") // UnitPrice -> Unit Price
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2"); // UOMAmount -> UOM Amount
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  };

  const orderItem = update.itemQuantityId ? items?.find(item => item.id === update.itemQuantityId) : null

  return (
    <Box key={`${update.id}-${update.itemQuantityId}`} sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="info.main"
        sx={{
          textDecoration: 'underline',
          cursor: 'pointer',
          display: 'block',
          width: '100%',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%'
        }}
        onClick={() => setHighlightedRow(update.itemQuantityId)}
      >
        {getItemDescription(update.itemQuantityId, items)}
      </Typography>
      <Box component="ul" sx={{ pl: 2, my: 0, ...listItemStyles }}>
        {(update.columnChanges || []).map((change, idx) => (
          change.columnName === 'Substitution' ? (
            <OrderLogItemSubstitution
              key={`${change.columnName}-${idx}`}
              logItem={update}
              orderItem={orderItem}
              items={items}
              setHighlightedRow={setHighlightedRow}
              change={change}
            />
          ) : change.columnName === 'InvoiceLog' ? (
            <Box key={`${change.columnName}-${idx}`}>
            {change.newValue?.split('\n').slice(1).filter(line => line.trim() !== "").map((line, lineIdx) => (
              <Box key={lineIdx} component="li" sx={{ typography: 'caption' }}>
                {line}
              </Box>
            ))}
          </Box>)
          : <Box key={`${change.columnName}-${idx}`} component="li" sx={{ typography: 'caption' }}>
            {`${formatColumnName(change.columnName)} changed from "${change.oldValue}" to "${change.newValue}"`}
          </Box>   
        ))}
      </Box>
    </Box>
  );
}

InvoiceItemDeletion.propTypes = {
  update: PropTypes.object,
};
function InvoiceItemDeletion({ update }) {
  return (update.columnChanges || []).map((change, idx) => (
    <Typography variant="caption" color="info.main" key={`${update.id}-${idx}`}>
      {change.newValue}
    </Typography>
  ));
}

InvoiceAddition.propTypes = {
  logItem: PropTypes.object,
  setHighlightedRow: PropTypes.func,
  items: PropTypes.array,
}
export default function InvoiceAddition({ logItem, setHighlightedRow, items }) {
  const [openDetails, setOpenDetails] = useState(false);
  const invoiceItemUpdates = logItem?.invoiceItemUpdates || [];

  const parsedInvoiceItemUpdates = parseLogItems(invoiceItemUpdates);

  return <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="body" color="info.main" sx={{wordBreak: 'break-word', whiteSpace: 'normal'}}>
        Invoice #{logItem.invoiceNumber} created {logItem.addedBy ? `by ${logItem.addedBy}` : ''}
      </Typography>
      <Box sx={{ flexGrow: 1 }} />
      {!!parsedInvoiceItemUpdates.length && (
        <IconButton
          size="small"
          onClick={() => setOpenDetails((prev) => !prev)}
          sx={{ p: 0.25, color: 'info.main' }}
        >
          <Iconify icon={openDetails ? 'mdi:chevron-down' : 'mdi:chevron-right'} width={16} height={16} />
        </IconButton>
      )}
    </Box>
    <Typography variant="body2" color="black">
      {fDateTimeWithTimezone(logItem.dateAdded)}
    </Typography>

    {/* Rendered while collapsed (just hidden) so log search can match the details */}
    {!!parsedInvoiceItemUpdates.length && (
      <Box sx={{ pl: 1, mb: 0.5, mt: 0.5, display: openDetails ? undefined : 'none' }}>
        {parsedInvoiceItemUpdates.map((update) => update.itemQuantityId > 0 ?
          <InvoiceItemUpdate update={update} setHighlightedRow={setHighlightedRow} items={items} key={`${update.id}-${update.itemQuantityId}`}/> :
          <InvoiceItemDeletion update={update} key={`${update.id}-${update.itemQuantityId}`} />)}
      </Box>
    )}
  </Box>
}